// @flow
import React from 'react';
import { connect } from 'react-redux';
import { Field, formValueSelector, reduxForm } from 'redux-form';
import { Prompt } from 'react-router-dom';

import StyledForm from '../shared/StyledForm';
import Scrollable from '../shared/Scrollable';

import Button from '../shared/Button';
import RadioGroup from '../shared/forms/RadioGroup';
import Select from '../shared/forms/Select';
import TextArea from '../shared/forms/TextArea';
import TextInput from '../shared/forms/TextInput';
import NumericInput from '../shared/forms/NumericInput';

export const validate = (values: Object) => {
    const errors = {};

    if (!values.agreementTypeId || values.agreementTypeId.length === 0) {
        errors.agreementTypeId = 'Agreement type is required';
    }

    if (!values.insuranceTypeId || values.insuranceTypeId.length === 0) {
        errors.insuranceTypeId = 'Insurance type is required';
    }

    if (Number.isNaN(values.rent)) {
        errors.rent = 'Rent per annum is required';
    }

    if (!values.size || parseInt(values.size, 10) < 1) {
        errors.size = 'Size is required';
    }

    if (
        Number.isNaN(values.rateableValue) ||
        parseInt(values.rateableValue, 10) < 0
    ) {
        errors.rateableValue = 'Rateable value is required';
    }

    return errors;
};

let PropertyKeyInformationForm = props => {
    const {
        handleSubmit,
        dirty,
        agreementTypeOptions,
        insuranceTypeOptions,
        selectedAgreementTypeId,
        selectedInsuranceTypeId,
        standardText,
        submitting,
    } = props;

    const commercialInsuranceOption = insuranceTypeOptions
        ? insuranceTypeOptions.find(option => option.label === 'Commercial')
        : null;

    const otherInsuranceOption = insuranceTypeOptions
        ? insuranceTypeOptions.find(option => option.label === 'Other')
        : null;

    const hasSelectedCommercialInsurance =
        selectedInsuranceTypeId &&
        commercialInsuranceOption &&
        selectedInsuranceTypeId === commercialInsuranceOption.id;

    const hasSelectedOtherInsurance =
        selectedInsuranceTypeId &&
        otherInsuranceOption &&
        selectedInsuranceTypeId === otherInsuranceOption.id;

    const agreementTypePlaceholder =
        selectedAgreementTypeId &&
        standardText.agreementTypes[selectedAgreementTypeId]
            ? standardText.agreementTypes[selectedAgreementTypeId]
                  .propertyAgreementTypeDescription
            : null;

    return (
        <StyledForm onSubmit={handleSubmit}>
            <Prompt
                when={dirty && !submitting}
                message="You have made changes that will be lost if you proceed. Are you sure you want to continue?"
            />

            <Scrollable additionalClassName="scroll-area">
                <div className="flex">
                    <div className="col">
                        <Field
                            name="keyDetails1"
                            label="Key Details 1"
                            component={TextInput}
                            disabled={submitting}
                        />

                        <Field
                            name="keyDetails2"
                            label="Key Details 2"
                            component={TextInput}
                            disabled={submitting}
                        />

                        <Field
                            name="keyDetails3"
                            label="Key Details 3"
                            component={TextInput}
                            disabled={submitting}
                        />

                        <Field
                            name="keyDetails4"
                            label="Key Details 4"
                            component={TextInput}
                            disabled={submitting}
                        />

                        <Field
                            name="keyDetails5"
                            label="Key Details 5"
                            component={TextInput}
                            disabled={submitting}
                        />

                        <Field
                            name="agreementTypeId"
                            label="Agreement Type"
                            component={Select}
                            disabled={submitting}
                        >
                            <option value="">Please select...</option>
                            {agreementTypeOptions.map(option =>
                                <option key={option.id} value={option.id}>
                                    {option.label}
                                </option>
                            )}
                        </Field>

                        <Field
                            name="agreementTypeDescription"
                            label="Agreement Description"
                            placeholder={agreementTypePlaceholder}
                            rows="8"
                            component={TextArea}
                            disabled={submitting}
                        />
                    </div>

                    <div className="col">
                        <Field
                            name="solutionsRPI"
                            label="Solutions RPI"
                            suffix="%"
                            step="0.1"
                            max={99}
                            component={NumericInput}
                            disabled={submitting}
                        />

                        <Field
                            name="rent"
                            label="Rent per annum (exc VAT)"
                            prefix="£"
                            component={NumericInput}
                            disabled={submitting}
                        />

                        <Field
                            name="rentText"
                            label="Alternative Rent Text"
                            component={TextInput}
                            disabled={submitting}
                        />

                        <Field
                            name="rateableValue"
                            label="Rateable Value"
                            prefix="£"
                            component={NumericInput}
                            disabled={submitting}
                        />

                        <Field
                            name="size"
                            label="Size"
                            suffix="sq ft"
                            component={NumericInput}
                            disabled={submitting}
                        />

                        <Field
                            name="insuranceTypeId"
                            label="Insurance Type"
                            component={Select}
                            disabled={submitting}
                        >
                            <option value="">Please select...</option>
                            {insuranceTypeOptions.map(option =>
                                <option key={option.id} value={option.id}>
                                    {option.label}
                                </option>
                            )}
                        </Field>

                        {hasSelectedOtherInsurance &&
                            <Field
                                name="insuranceTypeOther"
                                label="Other Insurance Description"
                                placeholder={
                                    otherInsuranceOption
                                        ? otherInsuranceOption.description
                                        : null
                                }
                                rows="8"
                                component={TextArea}
                                disabled={submitting}
                            />}

                        {hasSelectedCommercialInsurance &&
                            <Field
                                name="insuranceAmount"
                                label="Insurance Amount (Commercial Only)"
                                prefix="£"
                                min={1}
                                step={0.01}
                                component={NumericInput}
                                disabled={submitting}
                            />}

                        <Field
                            name="repairGuarantee"
                            label="Has Repair Guarantee?"
                            component={RadioGroup}
                            disabled={submitting}
                            options={[
                                {
                                    text: 'Yes',
                                    value: 'yes',
                                },
                                {
                                    text: 'No',
                                    value: 'no',
                                },
                            ]}
                        />
                    </div>
                </div>
            </Scrollable>

            <div className="sticky-footer">
                <Button type="submit" disabled={submitting}>
                    {submitting ? 'Saving...' : 'Save & Continue'}
                </Button>
            </div>
        </StyledForm>
    );
};

PropertyKeyInformationForm = reduxForm({
    form: 'propertyEditKeyInformation',
    validate,
})(PropertyKeyInformationForm);

const selector = formValueSelector('propertyEditKeyInformation');

const mapStateToProps = state => {
    return {
        selectedInsuranceTypeId: selector(state, 'insuranceTypeId'),
        selectedAgreementTypeId: selector(state, 'agreementTypeId'),
    };
};

export default connect(mapStateToProps)(PropertyKeyInformationForm);



// WEBPACK FOOTER //
// ./src/components/PropertyForms/PropertyKeyInformationForm.js